import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import PageTitle from '../headers/PageTitle';
import { MyTasksTable } from './MyTasksTable';
import {
  closedProgress,
  filterMyTasks,
  groupMyTasks,
  taskHref,
  type MyTaskGroup,
} from './myTasksTableModel';
import { useIncidentMyTasks, type MyTask } from './useMyTasks';

/**
 * Actions assigned to the signed-in user on this incident's action tracker.
 *
 * Same assignment rules and table as org `/my-tasks`, but scoped to one
 * workspace. Rows stay read-only and deep-link to the incident Actions page.
 */
const IncidentMyTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { incidentId } = useParams<{ incidentId: string }>();
  const { tasks, isLoading, isError } = useIncidentMyTasks(incidentId);
  const [search, setSearch] = useState('');

  const groups = useMemo(
    () => groupMyTasks(filterMyTasks(tasks, search)),
    [tasks, search]
  );
  const progress = useMemo(() => closedProgress(tasks), [tasks]);

  const emptyMessage = search.trim()
    ? `No tasks match “${search}”.`
    : 'Nothing on this incident is assigned to you.';

  if (!incidentId) {
    return <div>No incident ID provided</div>;
  }

  return (
    <div className="maincontent dark tw:text-foreground">
      <PageTitle title="My Tasks" />
      <p className="text-muted mb-2">
        Actions assigned to you, your ICS position, or your strike team on this
        incident.
      </p>
      {!isLoading && progress.total > 0 ? (
        <p className="text-muted tw:text-sm tw:tabular-nums mb-4">
          {progress.closed} of {progress.total} closed
        </p>
      ) : (
        <div className="mb-4" />
      )}

      {isError ? (
        <p className="text-danger">
          Your tasks could not be loaded. Refresh to try again.
        </p>
      ) : (
        <MyTasksTable
          groups={groups}
          search={search}
          onSearchChange={setSearch}
          onOpenTask={(task: MyTask) => navigate(taskHref(task))}
          onOpenGroup={(group: MyTaskGroup) => navigate(group.href)}
          emptyMessage={emptyMessage}
        />
      )}
    </div>
  );
};

export default IncidentMyTasksPage;
